import './SystemSettings.scss';
import {SystemSettingsProps} from './SystemSettings.model';
import {useEffect, useState} from 'react';
import UpdateButton from '../../shared/UpdateButton/UpdateButton';
import {
  Button,
  IconButton,
  Input,
  InputGroup,
  Message,
  RadioTile,
  RadioTileGroup,
  toaster,
  Toggle,
  Tooltip,
  Uploader,
  Whisper
} from 'rsuite';
import {CopyBtnIcon, TfiSaveIcon} from '../../../models/Icons.model';
import {BksApi} from '../../../services/Bks.api';
import NotificationFactory from '../../../shared/Notifications';
import {formatDate} from '../../../shared/Time.utils';
import ImageCropper from '../../shared/ImageCropper/ImageCropper';
import {SystemSettingsApi} from '../../../services/SystemSettings.api';
import {SharedApi} from '../../../services/Shared.api';
import StatusControl from "../../shared/StatusControl/StatusControl";
import PhoneInput from "../../shared/PhoneInput/PhoneInput";
import DeleteConfirmationModal from "../../modals/DeleteButtonWithConfirm/DeleteButtonWithConfirm";
import {useNavigate} from "react-router-dom";
import {PrimaryClockApi} from "../../../services/PrimaryClock.api";
import type {Common_UserDto} from "../../../api";
import {Esp} from "../../../Targets";
import {UpdateToLatest} from "../../../Commands";
import UsbModem from "./components/UsbModem";
import FirmwareUpdateIndicator from '../../shared/FirmwareUpdateIndicator/FirmwareUpdateIndicator';

const SystemSettings = ({device, getDeviceData, isAdmin, executeCommand}: SystemSettingsProps) => {
  const controlBlockId = device.id ?? '';
  const clockStationId = device.clockStation?.id ?? '';
  const [loading, setLoading] = useState<boolean>(false);
  const [article, setArticle] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [systemSerialNumber, setSystemSerialNumber] = useState('');
  const [clockStationModel, setClockStationModel] = useState('');
  const [clockStationSerialNumber, setClockStationSerialNumber] = useState('');
  const [clockStationSystemSerialNumber, setClockStationSystemSerialNumber] = useState('');
  const [clockStationChannels, setClockStationChannels] = useState('');
  const [description, setDescription] = useState('');
  const [privateDescription, setPrivateDescription] = useState('');
  const [autoUpdateState, setAutoUpdateState] = useState<boolean>(false);
  const [release, setRelease] = useState<boolean | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [bluetooth, setBluetooth] = useState(false);
  const [uartLogs, setUartLogs] = useState(false);
  const [bluetoothState, setBluetoothState] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [daysUntilMaintenance, setDaysUntilMaintenance] = useState('');
  const [daysUntilEndOfWarranty, setDaysUntilEndOfWarranty] = useState('');
  const [clockStationDaysUntilMaintenance, setClockStationDaysUntilMaintenance] = useState('');
  const [clockStationDaysUntilEndOfWarranty, setClockStationDaysUntilEndOfWarranty] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!device?.id) return;

    setBksValues();
    setClockStationValues();
    getFirmwareState();
    getBluetoothState();
    getSystemData();
  }, [device?.id]);

  useEffect(() => {
    setDaysUntilMaintenance(String(device.daysUntilMaintenance ?? 0));
    setDaysUntilEndOfWarranty(String(device.daysUntilEndOfWarranty ?? 0));
  }, [device.daysUntilMaintenance, device.daysUntilEndOfWarranty]);

  useEffect(() => {
    void getClockStationWarranty();
  }, [clockStationId]);

  const setBksValues = () => {
    setArticle(device?.article || '');
    setModel(device?.model || '');
    setSerialNumber(device?.serialNumber || '');
    setSystemSerialNumber(device?.clockSystemSerialNumber || '');
    setDescription(device?.description || '');
    setPrivateDescription(device?.privateDescription || '');
  };

  const setClockStationValues = () => {
    setClockStationModel(device.clockStation?.model || '');
    setClockStationSerialNumber(device.clockStation?.serialNumber || '');
    setClockStationSystemSerialNumber(device.clockStation?.clockSystemSerialNumber || '');
    setClockStationSystemSerialNumber(device.clockStation?.clockSystemSerialNumber || '');
    setClockStationChannels(String(device?.clockStation?.channels ?? ''));
  }

  const handleInputChange = (value: string, setter: (v: string) => void) => {
    setter(value);
  };

  const uploadFirmware = async () => {
    if (!device?.id || fileList.length === 0) {
      return;
    }

    const selectedFile = fileList[0]?.blobFile;

    if (!selectedFile) {
      toaster.push(
        <Message type="error" closable>
          Не удалось прочитать выбранный файл.
        </Message>,
        {
          duration: 3000,
          placement: 'topEnd'
        }
      );
      return;
    }

    setLoading(true);

    try {
      await SharedApi.uploadControlBlockFirmware(String(device.id), selectedFile);
      toaster.push(
        <Message type="success" closable>
          Команда на обновление успешно отправлена
        </Message>,
        {
          duration: 3000,
          placement: 'topEnd'
        }
      );
      setFileList([]);
    } catch (error) {
      console.error('Ошибка загрузки прошивки:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка загрузки прошивки.
        </Message>,
        {
          duration: 3000,
          placement: 'topEnd'
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    value: string,
    fieldName: string,
    apiMethod: (id: string, v: string) => Promise<boolean>,
    setter: (v: string) => void,
    id: string = controlBlockId
  ) => {
    setLoading(true);
    try {
      const result = await apiMethod(id, value);

      if (result) {
        setter(value);
        getDeviceData();
        toaster.push(
          NotificationFactory.success(`${fieldName} успешно обновлен`),
          {placement: 'topEnd'}
        );
      }
    } catch (e) {
      toaster.push(
        NotificationFactory.error(`Ошибка обновления ${fieldName}`),
        {placement: 'topEnd'}
      );
    } finally {
      setLoading(false);
    }
  };

  const onSaveArticle = () => {
    handleSave(article, 'Артикул', BksApi.updateArticle, setArticle);
  };

  const onSaveModel = () => {
    handleSave(model, 'Модель', BksApi.updateModel, setModel);
  };

  const onSaveSerialNumber = () => {
    handleSave(serialNumber, 'Серийный номер', BksApi.updateSerialNumber, setSerialNumber);
  };

  const onSaveSystemSerialNumber = () => {
    handleSave(
      systemSerialNumber,
      'Номер часовой системы',
      BksApi.updateSystemSerialNumber,
      setSystemSerialNumber
    );
  };

  const onSaveClockStationSerialNumber = () => {
    handleSave(
      clockStationSerialNumber,
      'Серийный номер (ClockStation)',
      SystemSettingsApi.updateSerialNumber,
      setClockStationSerialNumber,
      clockStationId
    );
  };

  const onSaveClockStationSystemSerialNumber = () => {
    handleSave(
      clockStationSystemSerialNumber,
      'Номер часовой системы (ClockStation)',
      SystemSettingsApi.updateSystemSerialNumber,
      setClockStationSystemSerialNumber,
      clockStationId
    );
  };

  const onSaveDescription = () => {
    handleSave(description, 'Описание', SystemSettingsApi.updateDescription, setDescription);
  };

  const onSavePrivateDescription = () => {
    handleSave(
      privateDescription,
      'Описание (Админ)',
      SystemSettingsApi.updatePrivateDescription,
      setPrivateDescription
    );
  };

  const getClockStationWarranty = async () => {
    if (!clockStationId) {
      setClockStationDaysUntilMaintenance('');
      setClockStationDaysUntilEndOfWarranty('');
      return;
    }

    try {
      const response = await SystemSettingsApi.getClockStationWarranty(clockStationId);
      // WarrantyDto is a combined request model: the API requires both fields
      // even when only one of them is being changed.
      setClockStationDaysUntilMaintenance(String(response.daysUntilMaintenance ?? 0));
      setClockStationDaysUntilEndOfWarranty(String(response.daysUntilEndOfWarranty ?? 0));
    } catch (error) {
      console.error('Ошибка получения данных гарантии ClockStation:', error);
      setClockStationDaysUntilMaintenance('');
      setClockStationDaysUntilEndOfWarranty('');
    }
  };

  const saveClockStationWarranty = async () => {
    if (!clockStationId || !isAdmin) return;

    setLoading(true);
    try {
      await SystemSettingsApi.updateClockStationWarranty(clockStationId, {
        daysUntilMaintenance: Number(clockStationDaysUntilMaintenance),
        daysUntilEndOfWarranty: Number(clockStationDaysUntilEndOfWarranty)
      });
      await getClockStationWarranty();
      toaster.push(
        NotificationFactory.success('Гарантия ClockStation обновлена'),
        {placement: 'topEnd'}
      );
    } catch (error) {
      console.error('Ошибка сохранения гарантии ClockStation:', error);
      toaster.push(
        NotificationFactory.error('Ошибка обновления гарантии ClockStation'),
        {placement: 'topEnd'}
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidDays = (value: string, fieldName: string): number | null => {
    if (value === '') {
      toaster.push(
        NotificationFactory.error(`Укажите количество дней: ${fieldName}`),
        {placement: 'topEnd'}
      );
      return null;
    }

    const days = Number(value);
    if (!Number.isSafeInteger(days) || days < 0) {
      toaster.push(
        NotificationFactory.error('Количество дней должно быть целым числом не меньше 0'),
        {placement: 'topEnd'}
      );
      return null;
    }

    return days;
  };

  const saveWarranty = async () => {
    if (!controlBlockId || !isAdmin) return;
    const days = getValidDays(daysUntilEndOfWarranty, 'гарантия');
    if (days === null) return;

    setLoading(true);
    try {
      await SystemSettingsApi.updateWarranty(controlBlockId, {daysUntilEndOfWarranty: days});
      await getDeviceData();
      toaster.push(
        NotificationFactory.success('Срок гарантии обновлён'),
        {placement: 'topEnd'}
      );
    } catch (error) {
      console.error('Ошибка сохранения гарантии:', error);
      toaster.push(
        NotificationFactory.error('Ошибка обновления гарантии'),
        {placement: 'topEnd'}
      );
    } finally {
      setLoading(false);
    }
  };

  const saveMaintenance = async () => {
    if (!controlBlockId || !isAdmin) return;
    const days = getValidDays(daysUntilMaintenance, 'техническое обслуживание');
    if (days === null) return;

    setLoading(true);
    try {
      await SystemSettingsApi.updateMaintenance(controlBlockId, {daysUntilMaintenance: days});
      await getDeviceData();
      toaster.push(
        NotificationFactory.success('Срок технического обслуживания обновлён'),
        {placement: 'topEnd'}
      );
    } catch (error) {
      console.error('Ошибка сохранения срока технического обслуживания:', error);
      toaster.push(
        NotificationFactory.error('Ошибка обновления срока технического обслуживания'),
        {placement: 'topEnd'}
      );
    } finally {
      setLoading(false);
    }
  };

  const changeAutoUpdateState = async (state: boolean) => {
    setLoading(true);
    try {
      const result = await SharedApi.updateAutoUpdate(controlBlockId, state);

      if (result) {
        setAutoUpdateState(state);
        toaster.push(NotificationFactory.success(), {
          placement: 'topEnd',
          duration: 3000
        });
      }
    } catch (error) {
      console.log(error);
      toaster.push(NotificationFactory.error(), {
        placement: 'topEnd',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const getSystemData = async () => {
    setLoading(true);
    try {
      const result = await PrimaryClockApi.getCoordinates(controlBlockId);
      if (result) {
        setUartLogs(result.debugOutput);
      }
    } catch (error) {
      console.error('Ошибка при получении:', error);
      toaster.push(NotificationFactory.error(), {
        placement: 'topEnd',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const getFirmwareState = async () => {
    setLoading(true);
    try {
      const result = await SharedApi.getFirmwareState(controlBlockId);
      if (result) {
        setAutoUpdateState(result.allowEspUpdate);
        setRelease(result.release);
      }
    } catch (error) {
      console.error('Ошибка при получении:', error);
      toaster.push(NotificationFactory.error(), {
        placement: 'topEnd',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const changeReleaseFirmwareState = async (state: boolean) => {
    setLoading(true);
    try {
      const result = await SharedApi.updateReleaseFirmwareState(controlBlockId, state);

      if (result) {
        setRelease(state);
        toaster.push(NotificationFactory.success(), {
          placement: 'topEnd',
          duration: 3000
        });
      }
    } catch (error) {
      console.log(error);
      toaster.push(NotificationFactory.error(), {
        placement: 'topEnd',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const onReboot = async () => {
    setLoading(true);
    try {
      const response = await SharedApi.rebootControlBlocks(controlBlockId);

      if (response) {
        toaster.push(NotificationFactory.success(), {
          placement: 'topEnd',
          duration: 3000
        });
      }
    } catch (error) {
      toaster.push(NotificationFactory.error(), {
        placement: 'topEnd',
        duration: 3000
      });
      console.error('Ошибка при перезапуске:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBluetooth = async (enabled: boolean) => {
    try {
      const result = await SharedApi.updateBluetoothState(controlBlockId, enabled);

      if (result) {
        getBluetoothState().then()
        toaster.push(
          NotificationFactory.success("Успешно"),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error("Ошибка обновления Bluetooth"),
        {placement: "topEnd"}
      );
    }
  };

  const getBluetoothState = async () => {
    try {
      const result = await SharedApi.getBluetoothState(controlBlockId);

      if (result) {
        setBluetooth(result.enabled)
        setBluetoothState(result.state)
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    onDelete().then();
    setShowDeleteModal(false);
  };

  const onDelete = async () => {
    setLoading(true);
    const response = await SystemSettingsApi.deleteDevice(controlBlockId);

    if (response) {
      toaster.push(
        NotificationFactory.success(),
        {placement: "topEnd"}
      );
    }

    setLoading(false);
    navigate('/devices');
  };

  const viewingUArtLogs = async (enabled: boolean) => {
    try {
      const result = await SystemSettingsApi.updateDebugOutput(controlBlockId, enabled);

      if (result) {

        getSystemData().then();

        toaster.push(
          NotificationFactory.success("Успешно"),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error("Ошибка обновления Bluetooth"),
        {placement: "topEnd"}
      );
    }
  }

  const sendCommand = async (e: any) => {
    e.preventDefault();

    setLoading(true);

    try {
      await executeCommand(Esp, UpdateToLatest, [])
    }
    catch (error) {
      console.log(error);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(controlBlockId);
      toaster.push(
        NotificationFactory.success("ID скопирован в буфер обмена"),
        {placement: "topEnd"}
      );
    } catch (err) {
      console.error('Ошибка при копировании: ', err);
      toaster.push(
        NotificationFactory.error("Не удалось скопировать"),
        {placement: "topEnd"}
      );
    }
  };

  return (
    <>
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Настройки системы</div>
        <section className="secondary-clock">
          <div className="row p-2 pb-0">
            <div className="col-12 text-end">
              <UpdateButton loading={loading} onRefresh={getDeviceData}/>
            </div>
          </div>
          <div className="system-settings__details">
              <div className="fieldset-wrapper control bg-white system-settings__description">
                <div className="legend-title">Описание</div>
                <div className="clock-wrapper w-100">
                  <InputGroup>
                    <InputGroup.Addon>Артикул</InputGroup.Addon>
                    <Input
                      value={article}
                      type="text"
                      onChange={(value) => handleInputChange(value, setArticle)}
                      onPressEnter={onSaveArticle}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveArticle}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Модель</InputGroup.Addon>
                    <Input
                      value={model}
                      type="text"
                      onChange={(value) => handleInputChange(value, setModel)}
                      onPressEnter={onSaveModel}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveModel}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Серийный номер ЧС</InputGroup.Addon>
                    <Input
                      value={serialNumber}
                      type="text"
                      onChange={(value) => handleInputChange(value, setSerialNumber)}
                      onPressEnter={onSaveSerialNumber}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveSerialNumber}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Номер часовой системы ЭМЧС</InputGroup.Addon>
                    <Input
                      value={systemSerialNumber}
                      type="text"
                      onChange={(value) => handleInputChange(value, setSystemSerialNumber)}
                      onPressEnter={onSaveSystemSerialNumber}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveSystemSerialNumber}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Версия прошивки</InputGroup.Addon>
                    <Input
                      readOnly
                      value={device?.clockStation?.firmwareVersion || ''}
                      type="text"
                    />
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Версия прошивки БКС</InputGroup.Addon>
                    <Input
                      readOnly
                      value={device?.firmwareVersion || ''}
                      type="text"
                    />
                    <FirmwareUpdateIndicator visible={device?.hasNewerFirmwareVersion}/>
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>ID</InputGroup.Addon>
                    <Input readOnly value={device?.id || ''} type="text"/>
                    <InputGroup.Button
                      style={{cursor: 'pointer'}}
                      onClick={handleCopyClick}
                      title="Сохранить"
                    >
                      <CopyBtnIcon/>
                    </InputGroup.Button>
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Последний онлайн</InputGroup.Addon>
                    <Input
                      readOnly
                      value={formatDate(device?.lastOnlineTime)}
                      type="text"
                    />
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Описание</InputGroup.Addon>
                    <Input
                      readOnly
                      value={device?.description || ''}
                      type="text"
                    />
                  </InputGroup>

                  {isAdmin && (
                    <InputGroup className="mt-2">
                      <InputGroup.Addon>Описание (Админ)</InputGroup.Addon>
                      <Input
                        readOnly
                        value={device?.privateDescription || ''}
                        type="text"
                      />
                    </InputGroup>
                  )}

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Номер Глонасс приемника</InputGroup.Addon>
                    <Input value={''} type="text"/>
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                </div>
              </div  >

              <div className="fieldset-wrapper control bg-white system-settings__clock-station">
                <div className="legend-title">Описание (ClockStation)</div>
                <div className="clock-wrapper w-100">
                  <InputGroup>
                    <InputGroup.Addon>Модель</InputGroup.Addon>
                    <Input value={clockStationModel} type="text"/>
                  </InputGroup>
                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Серийный номер</InputGroup.Addon>
                    <Input
                      value={clockStationSerialNumber}
                      type="text"
                      onChange={(value) => handleInputChange(value, setClockStationSerialNumber)}
                      onPressEnter={onSaveClockStationSerialNumber}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveClockStationSerialNumber}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Номер часовой системы</InputGroup.Addon>
                    <Input
                      value={clockStationSystemSerialNumber}
                      type="text"
                      onChange={(value) => handleInputChange(value, setClockStationSystemSerialNumber)}
                      onPressEnter={onSaveClockStationSystemSerialNumber}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        style={{cursor: 'pointer'}}
                        onClick={onSaveClockStationSystemSerialNumber}
                        title="Сохранить"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>
                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Каналы</InputGroup.Addon>
                    <Input
                      value={clockStationChannels}
                      type="text"
                    />
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дней до окончания гарантии</InputGroup.Addon>
                    <Input
                      readOnly={!isAdmin}
                      disabled={loading || !clockStationId}
                      value={clockStationDaysUntilEndOfWarranty}
                      inputMode="numeric"
                      type="text"
                      onChange={(value) => setClockStationDaysUntilEndOfWarranty(value.replace(/[^\d]/g, ''))}
                      onPressEnter={saveClockStationWarranty}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        disabled={loading || !clockStationId}
                        onClick={saveClockStationWarranty}
                        title="Сохранить срок гарантии"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дней до ТО</InputGroup.Addon>
                    <Input
                      readOnly={!isAdmin}
                      disabled={loading || !clockStationId}
                      value={clockStationDaysUntilMaintenance}
                      inputMode="numeric"
                      type="text"
                      onChange={(value) => setClockStationDaysUntilMaintenance(value.replace(/[^\d]/g, ''))}
                      onPressEnter={saveClockStationWarranty}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        disabled={loading || !clockStationId}
                        onClick={saveClockStationWarranty}
                        title="Сохранить срок технического обслуживания"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дней демо режима</InputGroup.Addon>
                    <Input/>
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Пароль демо режима</InputGroup.Addon>
                    <Input/>
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Число попыток ввода пароля</InputGroup.Addon>
                    <Input/>
                  </InputGroup>
                </div>
              </div>
            <div className="system-settings__image-column">
              <div className="fieldset-wrapper control bg-white">
                <div className="legend-title">Изменить изображение</div>
                <div className="clock-wrapper w-100">
                  <ImageCropper deviceId={device?.id} onCropComplete={getDeviceData}/>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Описание</div>
        <section className="secondary-clock">
          <div className="row p-2">
            <div className="col-12 d-flex justify-content-end gap-2">
              <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                <IconButton
                  style={{marginRight: '10px'}}
                  icon={<TfiSaveIcon/>}
                  size="sm"
                  onClick={onSaveDescription}
                  className="refresh-btn"
                />
              </Whisper>
              <UpdateButton  onRefresh={getDeviceData}/>
            </div>
          </div>
          <div className="row p-2 pt-0">
            <div className="col-md-12">
              <Input
                as="textarea"
                rows={3}
                value={description}
                onChange={(val: string) => setDescription(val)}
                onPressEnter={onSaveDescription}
                className="p-2"
              />
            </div>
          </div>
        </section>
      </div>

      {isAdmin && (
        <div className="fieldset-wrapper mb-2">
          <div className="legend-title main-legend-title">Описание ( Админ )</div>
          <section className="secondary-clock">
            <div className="row p-2">
              <div className="col-12 d-flex justify-content-end gap-2">
                <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                  <IconButton
                    style={{marginRight: '10px'}}
                    icon={<TfiSaveIcon/>}
                    size="sm"
                    onClick={onSavePrivateDescription}
                    className="refresh-btn"
                  />
                </Whisper>
                <UpdateButton  onRefresh={getDeviceData}/>
              </div>
            </div>
            <div className="row p-2 pt-0">
              <div className="col-md-12">
                <Input
                  as="textarea"
                  rows={3}
                  value={privateDescription}
                  onChange={(val: string) => setPrivateDescription(val)}
                  onPressEnter={onSavePrivateDescription}
                  className="p-2"
                />
              </div>
            </div>
          </section>
        </div>
      )}


      <div className="row mb-2">
        {isAdmin && (
        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper">
            <div className="legend-title main-legend-title">Обновление и питание устройства</div>
            <section className="secondary-clock">
              <div className="row p-2 pb-0">
                <div className="col-12 text-end">
                  <UpdateButton loading={loading} onRefresh={getDeviceData}/>
                </div>
              </div>
              <div className="row p-2">
                <div className="col-md-12 mb-2">
                  <Toggle
                    color="blue"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    loading={loading}
                    checked={!!autoUpdateState}
                    disabled={autoUpdateState === null || autoUpdateState === undefined}
                    onChange={changeAutoUpdateState}
                  />
                  <span className="radio-title" style={{marginLeft: 15}}>
                    {' '}
                    Авто-обновление
                  </span>
                </div>

                {isAdmin && (
                  <div className="col-md-12 mb-2">
                    <RadioTileGroup
                      className="w-100"
                      aria-label="Выбор типа прошивки"
                      value={release === null ? undefined : release ? 1 : 0}
                      disabled={loading || release === null}
                      onChange={(val) => changeReleaseFirmwareState(val === 1)}
                    >
                      <div className="row">
                        <div className="col-lg-12 col-md-12 mb-2">
                          <RadioTile
                            className="h-100 d-flex align-items-start"
                            value={1}
                            label="Релизная прошивка"
                          >
                            <span className="radio-tile-text">
                              Использовать релизную прошивку
                            </span>
                          </RadioTile>
                        </div>
                        <div className="col-lg-12 col-md-12 mb-2">
                          <RadioTile
                            className="h-100 d-flex align-items-start"
                            value={0}
                            label="Тестовая прошивка"
                          >
                            <span className="radio-tile-text">
                              Использовать тестовую прошивку
                            </span>
                          </RadioTile>
                        </div>
                      </div>
                    </RadioTileGroup>
                  </div>
                )}

                <div className="col-md-6 col-sm-6 text-center mb-2">
                  <Button
                    style={{width: 180}}
                    color="green"
                    appearance="primary"
                    type="button"
                    loading={loading}
                    disabled={loading}
                    onClick={sendCommand}
                  >
                    Обновить
                  </Button>
                </div>

                <div className="col-md-6 col-sm-6 text-center mb-2">
                  <Button
                    style={{width: 180}}
                    color="red"
                    appearance="primary"
                    type="button"
                    onClick={onReboot}
                    loading={loading}
                    disabled={loading}
                  >
                    Перезапустить
                  </Button>
                </div>
                <div className="col-md-6 text-center mb-2">
                  <Uploader
                    accept=".bin"
                    fileList={fileList}
                    autoUpload={false}
                    multiple={false}
                    listType="picture-text"
                    action=""
                    onChange={setFileList}
                  >
                    <Button
                      style={{width: 180}}
                      color="blue"
                      appearance="primary">
                      Выберите файл
                    </Button>
                  </Uploader>

                  <hr hidden={!fileList.length}/>

                  <Button
                    hidden={!fileList.length}
                    disabled={!fileList.length}
                    loading={loading}
                    onClick={uploadFirmware}
                  >
                    Загрузить
                  </Button>

                </div>
              </div>
            </section>
          </div>
        </div>)}

        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper">
            <div className="legend-title main-legend-title">Гарантия</div>
            <section className="secondary-clock">
              <div className="row mt-3 p-2">
                <div className="col-12">
                  <InputGroup>
                    <InputGroup.Addon>Дата окончания гарантии</InputGroup.Addon>
                    <Input
                      readOnly
                      value={device.warrantyExpirationDate ?? ''}
                      placeholder="Не задана"
                      type="text"
                    />
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дней до окончания гарантии</InputGroup.Addon>
                    <Input
                      readOnly={!isAdmin}
                      disabled={loading || !controlBlockId}
                      value={daysUntilEndOfWarranty}
                      inputMode="numeric"
                      type="text"
                      onChange={(value) => setDaysUntilEndOfWarranty(value.replace(/[^\d]/g, ''))}
                      onPressEnter={saveWarranty}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        disabled={loading || !controlBlockId}
                        onClick={saveWarranty}
                        title="Сохранить срок гарантии"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дата следующего ТО</InputGroup.Addon>
                    <Input
                      readOnly
                      value={device.nextMaintenanceDate ?? ''}
                      placeholder="Не задана"
                      type="text"
                    />
                  </InputGroup>

                  <InputGroup className="mt-2">
                    <InputGroup.Addon>Дней до ТО</InputGroup.Addon>
                    <Input
                      readOnly={!isAdmin}
                      disabled={loading || !controlBlockId}
                      value={daysUntilMaintenance}
                      inputMode="numeric"
                      type="text"
                      onChange={(value) => setDaysUntilMaintenance(value.replace(/[^\d]/g, ''))}
                      onPressEnter={saveMaintenance}
                    />
                    {isAdmin && (
                      <InputGroup.Button
                        disabled={loading || !controlBlockId}
                        onClick={saveMaintenance}
                        title="Сохранить срок технического обслуживания"
                      >
                        <TfiSaveIcon/>
                      </InputGroup.Button>
                    )}
                  </InputGroup>
                </div>
              </div>
            </section>
          </div>

          <div className="fieldset-wrapper mt-4">
            <div className="legend-title main-legend-title">Заголовок</div>
            <section className="secondary-clock">
              <div className="row mt-3 p-2">
                <div className="col-md-6 d-flex align-items-center">
                  <div>
                    <StatusControl status={bluetoothState ? 'online' : 'offline'}/>
                  </div>
                  <div style={{marginLeft: 10}}>
                    <Toggle
                      size={"sm"}
                      disabled={loading}
                      loading={loading}
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      checked={bluetooth}
                      onChange={(e) => toggleBluetooth(e)}
                    >
                      Вкл/Выкл Bluetooth
                    </Toggle>
                  </div>
                </div>
                <div className="col-md-6 mt-1">
                  <Toggle
                    size={"sm"}
                    disabled={loading}
                    loading={loading}
                    color="blue"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    checked={uartLogs}
                    onChange={(e) => viewingUArtLogs(e)}
                  >
                    Просмотр логов UArt
                  </Toggle>
                </div>
                <div className="col-md-12 mt-3">
                  <PhoneInput deviceId={controlBlockId}/>
                </div>
                <div className="col-md-12 mt-3 text-end">
                  {isAdmin && (
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="btn btn-outline-danger"
                      type="button"
                    >
                      Удалить устройство
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <UsbModem loading={loading} deviceId={controlBlockId}></UsbModem>

      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Список пользователей</div>
        <section className="secondary-clock">
          <div className="row p-2 mt-3">
            <div className="col-md-12">
              <>
                <div className="table-responsive-wrapper">
                  <table className="table">
                    <thead>
                    <tr>
                      <td>ID</td>
                      <td>Логин</td>
                      <td>Email</td>
                      <td>Телефон</td>
                    </tr>
                    </thead>
                    <tbody>
                    {device?.users?.map((user: Common_UserDto) => (
                      <tr key={user?.id}>
                        <td>{user?.id}</td>
                        <td>{user?.login}</td>
                        <td>
                          {user?.email ? (
                            <a href={`mailto:${user.email}`}>{user.email}</a>
                          ) : (
                            ''
                          )}
                        </td>
                        <td>
                          {user?.phone ? (
                            <a href={`tel:${user.phone}`}>{user.phone}</a>
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>


              </>
            </div>
          </div>
        </section>
      </div>


      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        itemName={`устройство ${device?.model} ${device?.clockStation?.serialNumber}`}
      />

    </>
  );
};

export default SystemSettings;
